/**
 * Checks if required cookie consent was given by user. If yes, displays child
 * [data-consent-given-content]; if no, displays child
 * [data-consent-missing-content] and adds listener that opens consent window
 * on [data-show-consent-button]. Depends on
 * https://www.drupal.org/project/eu_cookie_compliance/
 *
 * @example
 * <cookie-check data-required-consent="marketing,statistics">
 *     <template data-consent-given-content>
 *         {% if type === 'vimeo' %}
 *             <vimeo-component />
 *         {% else %}
 *             <youtube-component />
 *         {% endif %}
 *     </template>
 *     <template data-consent-missing-content>
 *         <p>Bitte Cookies annehmen</p>
 *         <button data-show-consent-button>Einstellungen ändern</button>
 *     </template>
 * </cookie-check>
 *
 */
class CookieCheck extends HTMLElement {

    #levels = [];
    #mutationObserver = null;
    #consentContent = null;
    #missingConsentContent = null;
    #updateConsentButton = null;

    async connectedCallback() {
        this.#consentContent = this.getTemplateContent('[data-consent-given-content]');
        this.#missingConsentContent = this.getTemplateContent('[data-consent-missing-content]');
        this.#updateConsentButton = this.getUpdateConsetButton(this.#missingConsentContent, '[data-show-consent-button]');
        this.#levels = this.getLevels();
        this.addUpdateConstentButtonClickListener();
        await this.waitUntilCookieComplianceIsReady();
        this.updateContentVisibility();
    }

    /**
     * Gets required consent levels from data-required-consent attribute and
     * returns them as an array
     * @return {string[]}
     */
    getLevels() {
        const levels = this.getAttribute('data-required-consent') || '';
        return levels.split(/,\s*/g);
    }

    /**
     * Returns a clone of the content of a <template> element.
     *
     * @param selector of the <template> element
     * @returns {Node|Element} clone of the template's content
     */
    getTemplateContent(selector) {
        const templateElement = this.querySelector(selector);

        if (!templateElement) {
            throw new Error(`CookieCheck.getTemplateContent: Child element with attribute ${selector} is missing.`);
        }

        // IE11 and its polyfill have issues with template.content (it is empty); use fallback
        // for IE11 by cloning the template itself, not its content.
        return templateElement.content ?
            templateElement.content.firstElementChild.cloneNode(true) :
            templateElement.cloneNode(true).firstElementChild;
    }

    getUpdateConsetButton(parent, selector) {
        if (!parent) return;

        const button = parent.querySelector(selector);
        if (!button) {
            throw new Error(`CookieCheck.getUpdateConsetButton(): Consent update button with selector ${selector} not found in children`);
        }
        return button;
    }

    /**
     * 🤯 Drupal.eu_cookie_compliance is not available on load. But when is it?
     * We don't know and cannot find out (there are no events/callbacks). Find
     * out the primitive way and 🙏.
     */
    waitUntilCookieComplianceIsReady() {
        let resolver;
        const promise = new Promise(resolve => resolver = resolve);
        const waitOneMoreTime = () => {
            if (Drupal.eu_cookie_compliance) resolver();
            setTimeout(waitOneMoreTime, 200);
        }
        waitOneMoreTime();
        return promise;
    }

    /**
     * Checks if all content
     */
    checkConsent() {
        return this.#levels.every(level => Drupal.eu_cookie_compliance.hasAgreed(level));
    }

    updateContentVisibility() {
        const consentGiven = this.checkConsent();

        // Make sure to only have a MutationObserver running while it's needed (as it hurts
        // performance)
        // Needed consent was not given and observer has not yet been setup: set it up
        if (!consentGiven && !this.#mutationObserver) {
            this.setupCookieBannerUpdateWatcher();
        }
        // If required consent is given and MutationObserver was setup previously: tear it down
        else if (consentGiven && this.#mutationObserver) {
            this.#mutationObserver.disconnect();
        }

        this.updateContent(consentGiven);
    }

    updateContent(consentGiven) {
        const elementToAdd = consentGiven ? this.#consentContent : this.#missingConsentContent;
        const elementToRemove = consentGiven ? this.#missingConsentContent : this.#consentContent;

        requestAnimationFrame(() => {
            if (this.contains(elementToRemove)) this.removeChild(elementToRemove);
            this.appendChild(elementToAdd);
            if (!consentGiven) this.addUpdateConstentButtonClickListener();
        });
    }

    addUpdateConstentButtonClickListener() {
        if (!this.#updateConsentButton) return;
        this.#updateConsentButton.addEventListener('click', this.handleConstentButtonClick.bind(this));
    }

    handleConstentButtonClick(event) {
        event.preventDefault();
        event.stopPropagation();
        this.displayConsentDialog();
    }

    /**
     * Cookie settings may have changed whenever the cookie banner is closed.
     * The cookie banner was not necessarily opened by
     * data-show-consent-button, but might have been open from the beginning or
     * have been opened by another button. Drupal.eu_cookie_compliance is
     * missing a stable API that provides hooks/events if settings are changed.
     * Therefore we sadly have to watch the DOM if the banner was removed
     * (it is not hidden but reoved when it is closed).
     */
    setupCookieBannerUpdateWatcher() {

        const domMutationCallback = (mutationsList) => {
            // Cookie banner was removed if any of the removedNodes is .js-cookie-banner
            const cookieBannerWasRemoved = mutationsList.some((mutation) => {
                const removedNodes = Array.from(mutation.removedNodes);
                return removedNodes.some((node) => {
                    // Non ELEMENT_NODE nodes don't have a querySelector and would throw on next
                    // line
                    if (node.nodeType !== Node.ELEMENT_NODE) return false;
                    return node.querySelector('.js-cookie-banner') !== null;
                });
            });

            if (cookieBannerWasRemoved) {
                this.updateContentVisibility();
            }
        };

        this.#mutationObserver = new MutationObserver(domMutationCallback);
        this.#mutationObserver.observe(document.querySelector('body'), { childList: true, subtree: true });
    }

    displayConsentDialog() {
        // make sure we don't show the cookie banner if it's already there
        // if we did buttons wouldn't work anymore
        if (document.querySelector('#sliding-popup')) return;

        Drupal.eu_cookie_compliance.createPopup(drupalSettings.eu_cookie_compliance.popup_html_info, false);
        Drupal.eu_cookie_compliance.initPopup();
    }
}

if (!window.customElements.get('cookie-check')) {
    window.customElements.define('cookie-check', CookieCheck);
}
